import AppLayout, { type AppLayoutProps } from "@cloudscape-design/components/app-layout";
import BreadcrumbGroup, { BreadcrumbGroupProps } from "@cloudscape-design/components/breadcrumb-group";
import SplitPanel from "@cloudscape-design/components/split-panel";
import Modal from "@cloudscape-design/components/modal";
import Flashbar from "@cloudscape-design/components/flashbar";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { useLayoutManager, useNavigation, useDebugPanel } from "@amzn/awsmp-max-agentic-renderer";
import styles from "./Layout.module.css";
import { ScrollToTop } from "@amzn/awsmp-max-ux-patterns/ScrollToTop";
import { DebugPanelContainer } from "@amzn/awsmp-max-ux-patterns/DebugPanel";
import type { BreadcrumbItem } from "@amzn/awsmp-max-agentic-renderer-interface";
import { BrowserCompatibilityWarning } from "./browser-compatibility";

export interface LayoutProps {
  children: React.ReactNode;
  dataAttributes?: Record<string, string>;
}

/**
 * Layout component - Reusable AppLayout wrapper
 *
 * This component provides a common AppLayout configuration that can be used
 * across different pages in the application, reducing code duplication and
 * ensuring consistent layout behavior.
 */
export const Layout: React.FC<LayoutProps> = ({ children, dataAttributes }) => {
  // Use layout manager for split panel, modal, and flashbar
  const { splitPanel, modal, flashbar, registerSticky } = useLayoutManager();
  const { useBreadcrumbs, navigateByHref, registry } = useNavigation();
  const storeBreadcrumbs = useBreadcrumbs();
  const location = useLocation();
  const debugPanel = useDebugPanel();

  // Create ref for breadcrumbs container
  const breadcrumbsRef = React.useRef<HTMLDivElement>(null);

  // Generate static breadcrumbs from route definitions when store is empty
  const staticBreadcrumbs: BreadcrumbItem[] = React.useMemo(() => {
    if (storeBreadcrumbs.length > 0) {
      return [];
    }

    const routeChain = registry.getBreadcrumbRoutes(location.pathname);
    return routeChain.map(({ route, params }) => ({
      text: route.getBreadcrumbText(params),
      href: route.link(params),
    }));
  }, [storeBreadcrumbs.length, registry, location.pathname]);

  // Use store breadcrumbs if available, otherwise use static breadcrumbs
  const breadcrumbs = storeBreadcrumbs.length > 0 ? storeBreadcrumbs : staticBreadcrumbs;

  // Close split panel when navigation occurs
  React.useEffect(() => {
    splitPanel.setShown(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- splitPanel is stable from Zustand store
  }, [location.pathname]);

  // Register breadcrumbs container as sticky element
  React.useEffect(() => {
    return registerSticky(breadcrumbsRef, "breadcrumbs");
  }, []);

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (event: CustomEvent<BreadcrumbGroupProps.ClickDetail<BreadcrumbItem>>) => {
    if (!event.detail.item.isAbsoluteUrl) {
      event.preventDefault();
      navigateByHref(event.detail.href);
    }
  };

  const handleBreadcrumbFollow = (event: CustomEvent<BreadcrumbGroupProps.ClickDetail<BreadcrumbItem>>) => {
    if (!event.detail.item.isAbsoluteUrl) {
      event.preventDefault();
      navigateByHref(event.detail.href);
    }
  };

  // Handle split panel toggle
  const handleSplitPanelToggle = ({ detail }: { detail: { open: boolean } }) => {
    splitPanel.setShown(detail.open);
  };

  const [splitPanelPreference, onSplitPanelPreferencesChange] = React.useState<AppLayoutProps.SplitPanelPosition>("side");

  // Initialize split panel position in metadata on mount
  React.useEffect(() => {
    splitPanel.updateMetadata({ position: splitPanelPreference });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, []);

  const showSplitPanel = splitPanel.shown && splitPanel.content;
  return (
    <>
      <ScrollToTop />
      <div className={styles.layout}>
        <AppLayout
          {...dataAttributes}
          navigationHide={true}
          contentType="default"
          disableContentPaddings={true}
          maxContentWidth={1024}
          breadcrumbs={
            <div id={"breadcrumb-ref"} ref={breadcrumbsRef}>
              <BreadcrumbGroup items={breadcrumbs} onClick={handleBreadcrumbClick} onFollow={handleBreadcrumbFollow} />
            </div>
          }
          content={
            <>
              <BrowserCompatibilityWarning />
              {children}
            </>
          }
          toolsHide={true}
          notifications={
            <Flashbar
              stackItems={true}
              items={flashbar.items.map((item) => ({
                ...item,
                onDismiss: item.dismissible !== false ? () => flashbar.removeItem(item.id) : undefined,
              }))}
              i18nStrings={{
                ariaLabel: "Notifications",
                errorIconAriaLabel: "Error",
                infoIconAriaLabel: "Information",
                inProgressIconAriaLabel: "In progress",
                successIconAriaLabel: "Success",
                warningIconAriaLabel: "Warning",
              }}
            />
          }
          splitPanel={
            showSplitPanel ? (
              <SplitPanel
                header={splitPanel.header}
                hidePreferencesButton={true}
                i18nStrings={{
                  preferencesTitle: "Split panel preferences",
                  preferencesPositionLabel: "Split panel position",
                  preferencesPositionDescription: "Choose the default split panel position for the service.",
                  preferencesPositionSide: "Side",
                  preferencesPositionBottom: "Bottom",
                  preferencesConfirm: "Confirm",
                  preferencesCancel: "Cancel",
                }}>
                {splitPanel.content}
              </SplitPanel>
            ) : undefined
          }
          splitPanelOpen={splitPanel.shown}
          splitPanelSize={splitPanel.metadata.size}
          splitPanelPreferences={{
            position: splitPanelPreference,
          }}
          onSplitPanelToggle={handleSplitPanelToggle}
          onSplitPanelResize={({ detail }) => splitPanel.updateMetadata({ size: detail.size })}
          onSplitPanelPreferencesChange={({ detail }) => {
            onSplitPanelPreferencesChange(detail.position);
            splitPanel.updateMetadata({ position: detail.position });
          }}
        />
      </div>
      {modal.shown && (
        <Modal visible={modal.shown} onDismiss={modal.hide} header={modal.header} size={modal.metadata.size}>
          {modal.content}
        </Modal>
      )}
      <DebugPanelContainer {...debugPanel} />
    </>
  );
};
